// AppShell - the persistent layout for any authenticated Govern360 page.
//
// Props:
//   navGroups        - [{ label, items: [{ id, label, icon, badge?, route }] }]
//   currentRoute     - string, used to highlight the active nav item
//   onNavigate(route) - callback when a nav item is clicked
//   topBar           - optional React node rendered in the top bar's right side
//   sidebarTop       - optional React node rendered at the top of the sidebar
//   children         - page content
//
// Nav groups are collapsible: click a group heading to roll it up/down. The
// collapsed/expanded state is REMEMBERED between visits (localStorage). The
// group containing the active route always stays open regardless.

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Search, Bell, HelpCircle, LogOut, ChevronDown, ChevronsLeft, ChevronsRight, Menu, X, Star } from 'lucide-react';
import { useAuth } from '../lib/authContext.jsx';
import LogoMark from './primitives/LogoMark.jsx';
import { APP_VERSION } from '../version.js'; import { synonymsFor } from '../lib/navSynonyms.js';
import { supabase } from '../lib/supabase.js'; // G360_K818 - favorites live on the profile

const STORE_KEY = 'g360.nav.collapsed.v2'; // G360_K776 - bump resets stale prefs to the new collapsed-by-default

/* G360_K817 / K818 - FAVORITES.
 *
 * Collette asked to be able to click a menu item into a favorites list.
 * Stored as an ARRAY, not a Set, so the order someone added them in is
 * the order they see - a set would reorder on reload and feel arbitrary.
 *
 * K818: profiles.nav_favorites is the SOURCE OF TRUTH, so favorites
 * follow the person between browsers and machines. localStorage stays,
 * but only as a cache - it lets the sidebar paint before the profile
 * has loaded, and it keeps the list usable if the write fails or the
 * tab is offline. Every read and write is wrapped: storage throws in
 * private windows and when site data is blocked, and a sidebar must
 * still render when it does. */
const FAV_KEY = 'g360.nav.favorites.v1';

function loadFavs() {
  try {
    const raw = localStorage.getItem(FAV_KEY);
    const arr = raw ? JSON.parse(raw) : null;
    if (Array.isArray(arr)) return arr.filter((x) => typeof x === 'string');
  } catch (e) { /* ignore - storage may be unavailable */ }
  return [];
}

function writeLocalFavs(list) {
  try { localStorage.setItem(FAV_KEY, JSON.stringify(list)); } catch (e) { /* ignore */ }
}

function loadCollapsed() {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) return new Set(JSON.parse(raw));
  } catch (e) { /* ignore - storage may be unavailable */ }
  return null; // G360_K776 - null = no saved preference yet (distinct from an empty set)
}

export default function AppShell({ navGroups, currentRoute, onNavigate, topBar, sidebarTop, sidebarFoot, children, onBellClick, onHelpClick }) {
  const { user, tenant, logout, appKind, profile } = useAuth();
  const isAdmin = appKind === 'admin';

  // G360_K776 - console sidebar starts with all nav groups rolled up (admin unchanged);
  // a saved preference is respected once the user expands/collapses anything.
  const [collapsed, setCollapsed] = useState(() => {
    const stored = loadCollapsed();
    if (stored) return stored;
    return isAdmin ? new Set() : new Set((navGroups || []).map((g) => g.label));
  });
  const [acctOpen, setAcctOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  // G360_K777 - desktop: fully hide the sidebar; a tab brings it back. Remembered per browser.
  const [sideHidden, setSideHidden] = useState(() => { try { return localStorage.getItem('g360.nav.sidehidden') === '1'; } catch (e) { return false; } });
  useEffect(() => { try { localStorage.setItem('g360.nav.sidehidden', sideHidden ? '1' : '0'); } catch (e) { /* ignore */ } }, [sideHidden]);

  /* G360_K818 - favorites. Paint from the local cache immediately, then
     adopt the profile once it arrives. */
  const [favs, setFavs] = useState(loadFavs);
  const favsAdopted = useRef(false);

  /* The RPC returns the list AS STORED, so a capped or rejected id shows
     up in the sidebar instead of the UI quietly disagreeing with the
     database. A failed write leaves the local cache in place: the person
     keeps their favorites for this session and the next load reconciles. */
  const saveFavs = useCallback(async (next) => {
    setFavs(next);
    writeLocalFavs(next);
    try {
      const { data, error } = await supabase.rpc('set_nav_favorites', { p_favorites: next });
      if (error) { console.warn('[govern360] favorites not saved to your profile:', error.message); return; }
      if (Array.isArray(data)) { setFavs(data); writeLocalFavs(data); }
    } catch (e) { /* offline - the cache still holds */ }
  }, []);

  useEffect(() => {
    if (!profile || favsAdopted.current) return;
    favsAdopted.current = true;
    const server = Array.isArray(profile.nav_favorites) ? profile.nav_favorites : null;
    if (server === null) {
      /* Never saved server-side. Anyone who favorited things under K817
         has them in this browser only - push those up rather than letting
         the first profile load silently wipe them. */
      const local = loadFavs();
      if (local.length) saveFavs(local);
      return;
    }
    setFavs(server);
    writeLocalFavs(server);
  }, [profile, saveFavs]);

  const toggleFav = (id) => saveFavs(favs.includes(id) ? favs.filter((x) => x !== id) : [...favs, id]);

  useEffect(() => { setMobileOpen(false); }, [currentRoute]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setMobileOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Persist whenever it changes.
  useEffect(() => {
    try { localStorage.setItem(STORE_KEY, JSON.stringify([...collapsed])); }
    catch (e) { /* ignore */ }
  }, [collapsed]);

  /* G360_K854 - open a group without toggling it shut. Clicking a rail icon
     must always land on that group expanded; toggle() would close it again
     for anyone whose last state had it open. */
  const openGroup = (label) =>
    setCollapsed((prev) => { const next = new Set(prev); next.delete(label); return next; });
  const toggle = (label) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });

  /* G360_K387 - this box has existed since the shell shipped and has
     never searched anything: an input with a placeholder, a Ctrl K hint,
     no onChange and no key listener. It is the same failure as a rules
     page nobody could find and a refresh button that gave no signal - a
     control that reads as working and is not.

     It searches the menu, because that is what somebody staring at a
     sidebar of sixty items in eight groups actually needs. Matching
     includes synonyms, so a page is found by what it does rather than by
     what we named it. */
  const [q, setQ] = useState('');
  const [hi, setHi] = useState(0);
  const searchRef = useRef(null);

  // Flattened once per nav change, not per keystroke. Each entry carries
  // its group so a result can say WHERE the page lives - somebody who
  // learns that stops needing the search for it.
  const flatNav = useMemo(() => {
    const out = [];
    for (const g of navGroups || []) {
      for (const it of g.items || []) {
        out.push({
          id: it.id,
          label: it.label,
          route: it.route,
          icon: it.icon,
          group: g.label,
          hay: [it.label, g.label, it.route, synonymsFor(it.id)].join(' ').toLowerCase(),
        });
      }
    }
    return out;
  }, [navGroups]);

  /* G360_K817 - the favorites group is a REAL nav group prepended to the
     list, not a second rendering path. Collapse, chevron, active
     highlighting and the mobile close all come for free, and there is no
     second copy of the row markup to drift from the first.

     Resolved through flatNav, so a favorite whose page has gone simply
     disappears instead of rendering a dead row. */
  const favItems = useMemo(
    () => favs.map((id) => flatNav.find((f) => f.id === id)).filter(Boolean),
    [favs, flatNav],
  );
  const groupsWithFavs = useMemo(
    () => (favItems.length ? [{ label: 'Favorites', icon: Star, items: favItems }, ...(navGroups || [])] : (navGroups || [])),
    [favItems, navGroups],
  );

  /* Every term must match somewhere. "token budget" finds Token
     Governance; "budget token" finds it too. Ranking puts a label match
     above a synonym match, so typing the real name never buries it under
     something that merely mentions the word. */
  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return [];
    const terms = needle.split(/\s+/).filter(Boolean);
    const scored = [];
    for (const item of flatNav) {
      if (!terms.every((t) => item.hay.includes(t))) continue;
      const lab = item.label.toLowerCase();
      let score = 3;
      if (lab.includes(needle)) score = 2;
      if (lab.startsWith(needle)) score = 1;
      scored.push({ ...item, score });
    }
    scored.sort((a, b) => a.score - b.score || a.label.localeCompare(b.label));
    return scored.slice(0, 8);
  }, [q, flatNav]);

  useEffect(() => { setHi(0); }, [q]);

  function goTo(item) {
    if (!item) return;
    onNavigate(item.route);
    setQ('');
    setMobileOpen(false);
    if (searchRef.current) searchRef.current.blur();
  }

  function onSearchKey(e) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi((i) => Math.min(i + 1, results.length - 1)); return; }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setHi((i) => Math.max(i - 1, 0)); return; }
    if (e.key === 'Enter')     { e.preventDefault(); goTo(results[hi]); return; }
    if (e.key === 'Escape')    { setQ(''); if (searchRef.current) searchRef.current.blur(); }
  }

  // The Ctrl K on the box was decoration. Now it is true.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        if (searchRef.current) { searchRef.current.focus(); searchRef.current.select(); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const initials = (user?.name || 'U')
    .split(' ')
    .map((s) => s[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className={'ap' + (sideHidden ? ' side-hidden' : '')}>
      {/* ---------- Sidebar ---------- */}
      <aside className={`ap-side ${mobileOpen ? 'mobile-open' : ''}`}>
        <div className="ap-brand">
          {/* G360_K653 - the organisation's own logo (Branding), when set */}
          {!isAdmin && tenant && /^(data:image\/png;base64,|https:\/\/)/.test(String(tenant.logo_url || ''))
            ? <img src={tenant.logo_url} alt="" data-testid="ap-org-logo" style={{ width: 32, height: 32, objectFit: 'contain', borderRadius: 6, flexShrink: 0 }} />
            : <LogoMark variant={isAdmin ? 'admin' : 'console'} size={32} />}
          <div className="ap-brand-text">
            <div className="ap-brand-n">
              {isAdmin ? 'Govern360' : tenant?.name || 'Govern360'}
              {isAdmin && <span className="admin-tag" style={{ marginLeft: 6 }}>Admin</span>}
            </div>
            <div className="ap-brand-s">
              {isAdmin ? 'Super-admin console' : 'Govern360 console'}
            </div>
          </div>
          {/* G360_K777 - desktop: hide the whole sidebar */}
          {/* G360_K854 - a TOGGLE, not a one-way hide. The rail needs its own
              way back; ap-side-reopen was a floating tab that only existed
              because the sidebar vanished entirely. */}
          <button className="ap-side-hide"
            aria-label={sideHidden ? 'Expand menu' : 'Collapse menu to icons'}
            title={sideHidden ? 'Expand menu' : 'Collapse menu to icons'}
            aria-expanded={!sideHidden}
            onClick={() => setSideHidden((v) => !v)}>
            {sideHidden ? <ChevronsRight size={18} /> : <ChevronsLeft size={18} />}
          </button>
        </div>

          <button className="ap-side-close" aria-label="Close menu" onClick={() => setMobileOpen(false)}>
            <X size={20} />
          </button>
        <div className="ap-side-top">{sidebarTop}</div>

        {isAdmin ? (
          <div className="ap-org-badge admin">
            <i className="ti ti-crown" />
            <span>All tenants - platform-wide</span>
          </div>
        ) : (
          tenant && (
            <div className="ap-org-badge">
              <i className="ti ti-building" />
              <span>{tenant.size ? `${tenant.size} - ` : ''}{tenant.plan || 'Trial'}</span>
            </div>
          )
        )}

        {/* G360_K130 - only the nav scrolls. The brand above and the
            footer below stay put, so the tenant name, sign-out and the
            version are reachable at any scroll position. */}
        <div className="ap-side-scroll">
        {groupsWithFavs.map((group) => {
          // The group holding the active route always stays open.
          const hasActive = group.items.some((it) => it.route === currentRoute);
          const isCollapsed = collapsed.has(group.label) && !hasActive;
          return (
            <div key={group.label}>
              <button
                type="button"
                className="ap-nav-group ap-nav-group-toggle"
                /* G360_K854 - the accessible name must NOT depend on the visible
                   text, because the rail hides it. Without this the nine rail
                   buttons announce as unlabelled, which is 4.1.2 and is also why
                   an icon rail is usually worse for a screen reader than the list
                   it replaced. title gives sighted users the same name on hover. */
                aria-label={group.label}
                title={group.label}
                /* In the rail a click means "take me there", not "fold this":
                   there is nothing visible to fold. It expands the sidebar and
                   opens that group. */
                onClick={() => { if (sideHidden) { setSideHidden(false); openGroup(group.label); } else { toggle(group.label); } }}
                aria-expanded={!isCollapsed}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  width: '100%', background: 'transparent', border: 0, cursor: 'pointer',
                  font: 'inherit', textAlign: 'left',
                }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  {group.icon && <group.icon size={14} style={{ opacity: 0.7, flexShrink: 0 }} />}
                  <span className="ap-nav-group-label">{group.label}</span>
                </span>
                <ChevronDown
                  size={14}
                  style={{
                    transition: 'transform 0.15s',
                    transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                    opacity: 0.6,
                  }}
                />
              </button>
              {!isCollapsed && group.items.map((item) => {
                const Icon = item.icon;
                const isActive = currentRoute === item.route;
                const isFav = favs.includes(item.id);
                /* G360_K853 - the one anchor every surface is guaranteed to have,
                   so generated guidance can point at a page that has no anchors of
                   its own. Pointing at the sidebar entry also teaches WHERE the page
                   lives, which is the thing people actually fail at.
                   Deliberately NOT set on the Favorites copy: groupsWithFavs
                   prepends a Favorites group built from these same items, so a
                   favourited page renders twice and a bare nav-<id> would match two
                   elements. resolve.js treats ambiguity as a stop, so BOTH would be
                   refused and the guide would point at nothing - the same defect as
                   binding ep-scope-rule inside a map over existing rules. The anchor
                   marks the item HOME in the nav; a favourite is a shortcut. */
                const navAnchor = group.label === 'Favorites' ? undefined : 'nav-' + item.id;
                /* G360_K817 - the star is a SIBLING of the nav button, inside a
                   positioned wrapper. A button nested in a button is invalid HTML
                   and makes the click target ambiguous; this gives each one an
                   unambiguous target, and .ap-nav keeps its own width and margin
                   so the full-row hover pill is unchanged. */
                return (
                  <div key={item.id} className="ap-navrow">
                    <button
                      className={`ap-nav ${isActive ? 'active' : ''}`}
                      data-testid={navAnchor}
                      onClick={() => { onNavigate(item.route); setMobileOpen(false); }}
                    >
                      {Icon && <Icon size={20} />}
                      {item.label}
                      {item.badge != null && (
                        <span
                          className="ap-nav-bdg"
                          style={item.badgeColor ? { background: item.badgeColor } : undefined}
                        >
                          {item.badge}
                        </span>
                      )}
                    </button>
                    <button
                      type="button"
                      className={`ap-fav ${isFav ? 'on' : ''}`}
                      aria-pressed={isFav}
                      title={isFav ? `Remove ${item.label} from Favorites` : `Add ${item.label} to Favorites`}
                      aria-label={isFav ? `Remove ${item.label} from Favorites` : `Add ${item.label} to Favorites`}
                      onClick={(e) => { e.stopPropagation(); toggleFav(item.id); }}
                    >
                      <Star size={14} fill={isFav ? 'currentColor' : 'none'} />
                    </button>
                  </div>
                );
              })}
            </div>
          );
        })}
        </div>

        {/* G360_K131 - the avatar, name, role and a second sign-out lived
            here AND in the top-right account menu, which also carries the
            email, My Account and Team. Two controls for one job, and this
            was the smaller one. The footer keeps the version only. */}
        <div className="ap-side-foot">

          {/* G360_K360 - the footer is the one region pinned at every scroll
              position, so it is where a control that must always be reachable
              belongs. Passed in like sidebarTop, so this shared shell imports
              nothing app-specific and the admin app is unaffected. */}
          {sidebarFoot}

          {/* G360_K129 - the version belongs INSIDE ap-side-foot, under the
              user row and the same top border. Kit 128 rendered it after
              the closing div, so it hung below the divider on its own. */}
          <div style={{
            fontSize: 11, lineHeight: 1.4,
            color: 'var(--g-text-3)', letterSpacing: '.02em',
          }}>
            Govern360 {APP_VERSION}
          </div>
        </div>
      </aside>

      {/* G360_K777 - desktop: bring the hidden sidebar back */}
      <button className="ap-side-reopen" aria-label="Show menu" title="Show menu" onClick={() => setSideHidden(false)}><ChevronsRight size={18} /></button>

      {/* ---------- Main ---------- */}
      <main className="ap-main">
        <header className="ap-top">
          <button className="ap-hamburger" aria-label="Open menu" onClick={() => setMobileOpen(true)}>
            <Menu size={22} />
          </button>
          <div className="ap-search">
            <Search size={20} />
            {/* G360_K387 */}             <input               ref={searchRef}               value={q}               onChange={(e) => setQ(e.target.value)}               onKeyDown={onSearchKey}               placeholder="Search pages - try alerts, tokens, evidence"               aria-label="Search pages"               autoComplete="off"             />
            <kbd>Ctrl K</kbd>             {q.trim() !== '' && (               <div className="ap-search-res">                 {results.length === 0 ? (                   /* G360_K387 - says what was searched. "No results" alone                      leaves somebody unsure whether we searched the menu, the                      data, or nothing at all. */                   <div className="ap-search-empty">No page matches "{q.trim()}". This searches the menu, not your data.</div>                 ) : results.map((r, idx) => {                   const RIcon = r.icon;                   return (                     <button                       key={r.id}                       type="button"                       className={`ap-search-item ${idx === hi ? 'hi' : ''}`}                       onMouseEnter={() => setHi(idx)}                       onMouseDown={(e) => e.preventDefault()}                       onClick={() => goTo(r)}                     >                       {RIcon && <RIcon size={16} />}                       <span className="ap-search-lab">{r.label}</span>                       <span className="ap-search-grp">{r.group}</span>                     </button>                   );                 })}               </div>             )}
          </div>
          <div className="ap-top-right">
            {topBar}
            <button className="ap-ic" aria-label="Notifications" onClick={() => (onBellClick ? onBellClick() : (window.location.hash = '#risks'))}>
              <Bell size={20} />
            </button>
            <button className="ap-ic ap-ic-help" aria-label="Help" onClick={() => (onHelpClick ? onHelpClick() : (window.location.hash = '#help'))}>
              <HelpCircle size={20} />
            </button>
            <div style={{ position: 'relative' }}>
              <button onClick={() => setAcctOpen((v) => !v)} aria-label="Account menu"
                style={{ display: 'flex', alignItems: 'center', gap: 8, border: 0, background: 'transparent', cursor: 'pointer', padding: 4 }}>
                <div className={`ap-av ${isAdmin ? 'admin' : ''}`}>{initials}</div>
                <span className="ap-acct-name" style={{ fontWeight: 500, fontSize: 14 }}>{user?.name}</span>
                <ChevronDown size={16} style={{ color: 'var(--g-text-2)' }} />
              </button>
              {acctOpen && (
                <>
                  <div onClick={() => setAcctOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
                  <div className="acct-menu" style={{ position: 'absolute', right: 0, top: 52, width: 260, background: 'var(--g-surface)', border: '1px solid var(--g-divider)', borderRadius: 'var(--g-r-md)', boxShadow: 'var(--g-e2)', zIndex: 41, overflow: 'hidden' }}>
                    <div style={{ padding: 16, borderBottom: '1px solid var(--g-divider)' }}>
                      <div style={{ fontWeight: 600, fontSize: 15 }}>{user?.name || 'User'}</div>
                      <div style={{ fontSize: 13, color: 'var(--g-text-2)', marginTop: 2 }}>{user?.email || ''}</div>
                      <div style={{ marginTop: 8 }}><span className="pill p-info" style={{ textTransform: 'capitalize' }}>{user?.role || 'member'}</span></div>
                    </div>
                    <button onClick={() => { setAcctOpen(false); window.location.hash = '#account'; }}
                      style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '11px 16px', border: 0, background: 'transparent', cursor: 'pointer', fontSize: 14, textAlign: 'left' }}>
                      <ChevronDown size={16} style={{ transform: 'rotate(-90deg)', color: 'var(--g-text-2)' }} />My Account
                    </button>
                    <button onClick={() => { setAcctOpen(false); window.location.hash = '#users'; }}
                      style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '11px 16px', border: 0, background: 'transparent', cursor: 'pointer', fontSize: 14, textAlign: 'left' }}>
                      <ChevronDown size={16} style={{ transform: 'rotate(-90deg)', color: 'var(--g-text-2)' }} />Team
                    </button>
                    <button onClick={() => { setAcctOpen(false); logout(); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '11px 16px', border: 0, borderTop: '1px solid var(--g-divider)', background: 'transparent', cursor: 'pointer', fontSize: 14, textAlign: 'left', color: 'var(--g-danger)' }}>
                      <LogOut size={16} />Sign Out
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        <section className="ap-page">{children}</section>
      </main>

      <div className={'ap-side-backdrop ' + (mobileOpen ? 'open' : '')} onClick={() => setMobileOpen(false)} />
    </div>
  );
}
